import { useMemo } from 'react';
import styled from 'styled-components';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { useSettings } from '@wu/widget-core';
import classNames from 'classnames';
import Color from 'color';
import { APP_CLASSNAME_PREFIX } from '../../../../constants';
import { getPerevedentsevHeight } from '../../../helpers';

const ButtonElement = ({ onPress, ...props }) => {
  const handleClick = (e) => {
    onPress?.(e);
  };

  const handleKeyDown = (e) => {
    const { key } = e;

    if (key === 'Enter' || key === ' ') {
      e.preventDefault();
      onPress?.(e);
    }
  };

  return <Block onClick={handleClick} onKeyDown={handleKeyDown} {...props} />;
};

export const Button = ({
  googleAnalyticsExtraParameters,
  data,
  isDrag,
  isMobile
}) => {
  const {
    contentButtonText,
    contentButtonLink,
    contentButtonStyle,
    contentButtonShape,
    contentButtonColor,
    contentButtonFontSize
  } = data;

  const { editMode, googleAnalytics } = useSettings();
  const buttonStyleIsOutline = contentButtonStyle === 'outline';
  const buttonFontSizeNumber = parseInt(
    isMobile ? 16 : contentButtonFontSize,
    10
  );
  const buttonHeight = getPerevedentsevHeight(buttonFontSizeNumber);

  const buttonColorIsDark = useMemo(
    () => new Color(contentButtonColor).isDark(),
    [contentButtonColor]
  );
  const buttonFontColor = useMemo(
    () => (buttonColorIsDark ? 'rgb(255, 255, 255)' : 'rgb(17, 17, 17)'),
    [buttonColorIsDark]
  );
  const buttonColorHover = useMemo(() => {
    const baseColor = new Color(contentButtonColor);
    const mixColor = buttonColorIsDark ? Color('white') : Color('black');

    return baseColor.mix(mixColor, 0.1).hex();
  }, [contentButtonColor, buttonColorIsDark]);

  const handlePress = (e) => {
    const { rawValue: link } = contentButtonLink ?? {};
    if (!isDrag && !!link) {
      const target =
        editMode || contentButtonLink.target === '_blank' ? '_blank' : '_top';
      const win = window.open(link, target);
      win?.focus();
    }

    const valueMapping = {
      ...googleAnalyticsExtraParameters,
      buttonText: contentButtonText,
      buttonUrl: link
    };

    sendGoogleAnalyticsEvents({
      eventType: 'buttonClicked',
      googleAnalyticsEvents: googleAnalytics,
      valueMapping
    });

    e.stopPropagation();
  };

  return (
    <Container $isOutline={buttonStyleIsOutline}>
      <StyledButton
        onPress={handlePress}
        $height={buttonHeight}
        $fontSize={buttonFontSizeNumber}
        $shape={contentButtonShape}
        $isOutline={buttonStyleIsOutline}
        $contentColor={contentButtonColor}
        $fontColor={buttonFontColor}
        $hoverColor={buttonColorHover}
      >
        {contentButtonText}
      </StyledButton>
    </Container>
  );
};

const Block = styled.div.attrs({
  role: 'button',
  tabIndex: 0
})`
  display: inline-flex;
  cursor: pointer;
`;

const Container = styled.div.attrs(({ $isOutline }) => ({
  className: classNames(`${APP_CLASSNAME_PREFIX}-button-component`, {
    [`${APP_CLASSNAME_PREFIX}-button-styleOutline`]: $isOutline
  })
}))`
  margin: 28px 0;
`;

const StyledButton = styled(ButtonElement).attrs({
  className: `${APP_CLASSNAME_PREFIX}-button-link`
})`
  overflow: hidden;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
  padding: 0.75em 2.375em;
  outline: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1;
  transition-property: border-color, background-color, color;
  transition-duration: 0.2s;
  transition-timing-function: ease;
  font-weight: 700;
  border-style: solid;

  height: ${({ $height }) => $height}px;
  font-size: ${({ $fontSize }) => $fontSize}px;
  border-radius: ${({ $shape }) => $shape}px;

  border-width: ${({ $isOutline }) => ($isOutline ? '2px' : '0')};
  border-color: ${({ $isOutline, $contentColor, $fontColor }) =>
    $isOutline ? $contentColor : $fontColor};
  background-color: ${({ $isOutline, $contentColor }) =>
    $isOutline ? 'transparent' : $contentColor};
  color: ${({ $isOutline, $contentColor, $fontColor }) =>
    $isOutline ? $contentColor : $fontColor};

  &:hover {
    border-color: ${({ $isOutline, $hoverColor, $fontColor }) =>
      $isOutline ? $hoverColor : $fontColor};
    background-color: ${({ $isOutline, $hoverColor }) =>
      $isOutline ? 'transparent' : $hoverColor};
    color: ${({ $isOutline, $hoverColor, $fontColor }) =>
      $isOutline ? $hoverColor : $fontColor};
  }
`;
